import * as React from "react";
import { Folder, Pencil, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { RequestCategory } from "@/types/research.types";
import { useDeleteRequestCategory } from "@/hooks/useDeleteRequestCategory";
import { useDeleteRequest } from "@/hooks/useDeleteRequest";

interface ConfirmDeleteDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  onConfirm: () => void;
}

export const ConfirmDeleteDialog: React.FC<ConfirmDeleteDialogProps> = ({
  isOpen,
  onOpenChange,
  title,
  description,
  onConfirm,
}) => (
  <Dialog open={isOpen} onOpenChange={onOpenChange}>
    <DialogContent className="p-4">
      <DialogHeader className="sm:text-center">
        <DialogTitle className="text-base font-medium text-center">{title}</DialogTitle>
        <DialogDescription className="text-center text-sm">{description}</DialogDescription>
      </DialogHeader>
      <DialogFooter className="flex-row gap-2 sm:justify-center">
        <Button variant="secondary" onClick={() => onOpenChange(false)} className="flex-1">
          Cancel
        </Button>
        <Button variant="destructive" onClick={onConfirm} className="flex-1">
          Delete
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

interface CategoryRowProps {
  category: RequestCategory;
  onClick: () => void;
  onEdit: (category: RequestCategory) => void;
  onDelete: (e: React.MouseEvent, categoryId: string) => void;
}

export const CategoryRow: React.FC<CategoryRowProps> = ({
  category,
  onClick,
  onEdit,
  onDelete,
}) => (
  <div
    className="flex items-center justify-between px-3 py-2 bg-primary-100 rounded-lg transition-all cursor-pointer"
    onClick={onClick}
  >
    <div className="flex items-center gap-2 flex-1 min-w-0">
      <Folder className="size-3.5 text-neutral-400" />
      <p className="text-sm font-medium text-neutral-400 truncate">{category.name}</p>
    </div>
    <div className="flex items-center gap-3 ml-2">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              className="w-auto h-auto p-0"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(category);
              }}
            >
              <Pencil className="size-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Edit category</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              className="w-auto h-auto p-0"
              size="icon"
              onClick={(e) => onDelete(e, category._id)}
            >
              <Trash2 className="size-3.5 text-danger-200" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Delete category</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  </div>
);

export const StaticCategoryRow: React.FC<{ label: string; onClick: () => void }> = ({
  label,
  onClick,
}) => (
  <div
    className="flex items-center justify-between px-3 py-2 bg-primary-100 rounded-lg transition-all cursor-pointer"
    onClick={onClick}
  >
    <div className="flex items-center gap-2 flex-1 min-w-0">
      <Folder className="size-3.5 text-neutral-400" />
      <p className="text-sm font-medium text-neutral-400 truncate">{label}</p>
    </div>
  </div>
);

export function getCategoriesToShow(
  searchQuery: string,
  filteredCategories: RequestCategory[],
  categories: RequestCategory[] | undefined,
  currentParentId: string | null
): RequestCategory[] {
  if (searchQuery) {
    return [...filteredCategories].reverse();
  }
  if (!categories) return [];
  const filtered =
    currentParentId != null
      ? categories.filter((c) => c.parent_id === currentParentId)
      : categories.filter((c) => c.parent_id == null);
  return [...filtered].reverse();
}

export function useDeleteHandlers() {
  const deleteCategoryMutation = useDeleteRequestCategory();
  const deleteRequestMutation = useDeleteRequest();

  const [isDeleteCategoryDialogOpen, setIsDeleteCategoryDialogOpen] = React.useState(false);
  const [deletingCategoryId, setDeletingCategoryId] = React.useState<string | null>(null);
  const [isDeleteRequestDialogOpen, setIsDeleteRequestDialogOpen] = React.useState(false);
  const [deletingRequestId, setDeletingRequestId] = React.useState<string | null>(null);

  const handleDeleteCategory = (e: React.MouseEvent, categoryId: string) => {
    e.stopPropagation();
    setDeletingCategoryId(categoryId);
    setIsDeleteCategoryDialogOpen(true);
  };

  const confirmDeleteCategory = () => {
    if (deletingCategoryId != null) {
      deleteCategoryMutation.mutate(deletingCategoryId);
      setIsDeleteCategoryDialogOpen(false);
      setDeletingCategoryId(null);
    }
  };

  const handleDeleteRequest = (e: React.MouseEvent, requestId: string) => {
    e.stopPropagation();
    setDeletingRequestId(requestId);
    setIsDeleteRequestDialogOpen(true);
  };

  const confirmDeleteRequest = () => {
    if (deletingRequestId != null) {
      deleteRequestMutation.mutate(deletingRequestId);
      setIsDeleteRequestDialogOpen(false);
      setDeletingRequestId(null);
    }
  };

  return {
    isDeleteCategoryDialogOpen,
    setIsDeleteCategoryDialogOpen,
    isDeleteRequestDialogOpen,
    setIsDeleteRequestDialogOpen,
    handleDeleteCategory,
    confirmDeleteCategory,
    handleDeleteRequest,
    confirmDeleteRequest,
  };
}
